import React from "react";
import ProccessingSVG from "../../components/animation/Processing";
import { createPortal } from "react-dom";

import "./loading.scss";

const loadingRoot = document.getElementById("loading-root") as HTMLElement;

interface LoadingContextInterface {
  loading: boolean;
  showLoading: Function;
  hideLoading: Function;
}

const LoadingContext = React.createContext<LoadingContextInterface>({
  loading: false,
  showLoading: () => {},
  hideLoading: () => {},
});

function LoadingElement() {
  return createPortal(
    <>
      <div className="backing loading-backing" />
      <div className="loading">
        <ProccessingSVG />
      </div>
    </>,
    loadingRoot
  );
}

function LoadingStore({ children }: { children: JSX.Element | JSX.Element[] }) {
  const [loading, changeLoading] = React.useState(false);

  const showLoading = React.useCallback(() => {
    changeLoading(true);
  }, []);

  const hideLoading = React.useCallback(() => {
    setTimeout(() => {
      changeLoading(false);
    }, 100);
  }, []);

  const ctx = React.useMemo(
    () => ({
      loading,
      showLoading,
      hideLoading,
    }),
    [hideLoading, loading, showLoading]
  );

  return (
    <LoadingContext.Provider value={ctx}>
      {loading && <LoadingElement />}
      {children}
    </LoadingContext.Provider>
  );
}

function useLoading() {
  const { showLoading, hideLoading } = React.useContext(LoadingContext);

  const processingAsyncFn = React.useCallback(
    async (fn: Function) => {
      try {
        showLoading();
        return await fn();
      } catch (e) {
        hideLoading();
        throw e;
      } finally {
        hideLoading();
      }
    },
    [hideLoading, showLoading]
  );

  return processingAsyncFn;
}

export { LoadingStore, useLoading };
